import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, ArrowRight, ArrowsClockwise, CheckCircle, CheckSquare, Lightbulb, Question, Square, Trophy, XCircle } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Animated, Dimensions, Platform, ScrollView, StyleSheet, TouchableOpacity, Vibration, View } from 'react-native';
import ConfettiCannon from 'react-native-confetti-cannon';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors - VIOLET pour Prof Kylian
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  emerald700: '#065F46',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  rose700: '#BE123C',
  blue500: '#3B82F6',
  blue100: '#DBEAFE',
  purple600: '#7C3AED',
};

// Types pour Prof Kylian Quiz
interface QuestionType {
  question: string;
  answers: string[];
  correctIndex?: number;
  correctIndexes?: number[];
  type?: 'single' | 'multiple';
  explanation?: string;
}

interface ShuffledQuestion extends QuestionType {
  shuffledChoices: string[];
  shuffledCorrectAnswers: number[];
  isMultiple: boolean;
}

interface QuizData {
  id: string;
  title: string;
  matiere: string;
  questions: QuestionType[];
  is_free: boolean;
}

const shuffleArray = <T,>(array: T[]): { shuffled: T[]; indexMap: number[] } => {
  const shuffled = [...array];
  const indexMap = array.map((_, i) => i);
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    [indexMap[i], indexMap[j]] = [indexMap[j], indexMap[i]];
  }
  
  return { shuffled, indexMap };
};

export default function ProfKylianQuizScreen() {
  const { id } = useLocalSearchParams();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [questions, setQuestions] = useState<ShuffledQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  
  const [hasAnswered, setHasAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [loading, setLoading] = useState(true);

  const confettiRef = useRef<any>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const resultScaleAnim = useRef(new Animated.Value(0)).current;
  const optionAnims = useRef<Animated.Value[]>([]).current;

  useEffect(() => {
    fetchQuiz();
  }, [id]);

  useEffect(() => {
    if (questions.length > 0) {
      const currentChoices = questions[currentIndex]?.shuffledChoices || [];
      while (optionAnims.length < currentChoices.length) {
        optionAnims.push(new Animated.Value(0));
      }
      animateOptionsIn();
    }
  }, [questions, currentIndex]);

  useEffect(() => {
    if (questions.length > 0) {
      Animated.timing(progressAnim, {
        toValue: ((currentIndex + 1) / questions.length) * 100,
        duration: 500,
        useNativeDriver: false,
      }).start();
    }
  }, [currentIndex, questions.length]);

  useEffect(() => {
    if (isFinished && questions.length > 0) {
      const percentage = Math.round((score / questions.length) * 100);
      if (percentage >= 80) {
        setShowConfetti(true);
        setTimeout(() => {
          confettiRef.current?.start();
        }, 300);
      }
    }
  }, [isFinished, score, questions.length]);

  const animateOptionsIn = () => {
    const currentChoices = questions[currentIndex]?.shuffledChoices || [];
    optionAnims.forEach(anim => anim.setValue(0));
    
    const animations = currentChoices.map((_, index) => {
      return Animated.timing(optionAnims[index] || new Animated.Value(0), {
        toValue: 1,
        duration: 300,
        delay: index * 100,
        useNativeDriver: true,
      });
    });
    
    Animated.parallel(animations).start();
  };

  const animateQuestionTransition = (direction: 'next' | 'prev', callback: () => void) => {
    const toValue = direction === 'next' ? -50 : 50;
    
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: toValue,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      callback();
      slideAnim.setValue(direction === 'next' ? 50 : -50);
      
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  };

  const animateSelection = () => {
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const animateResult = () => {
    Animated.spring(resultScaleAnim, {
      toValue: 1,
      friction: 4,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  // ✅ Extraction et shuffle des questions - Format Prof Kylian
  const extractAndShuffleQuestions = (rawQuestions: QuestionType[]): ShuffledQuestion[] => {
    if (!rawQuestions || !Array.isArray(rawQuestions)) return [];

    return rawQuestions.map((q) => {
      const choices = q.answers || [];
      const { shuffled, indexMap } = shuffleArray(choices);
      
      // Déterminer les bonnes réponses
      let originalCorrectAnswers: number[] = [];
      
      if (q.correctIndexes && Array.isArray(q.correctIndexes)) {
        originalCorrectAnswers = q.correctIndexes;
      } else if (typeof q.correctIndex === 'number') {
        originalCorrectAnswers = [q.correctIndex];
      } else {
        originalCorrectAnswers = [0];
      }
      
      const shuffledCorrectAnswers = originalCorrectAnswers.map(
        originalIndex => indexMap.indexOf(originalIndex)
      );
      
      const isMultiple = q.type === 'multiple' || originalCorrectAnswers.length > 1;
      
      return {
        ...q,
        shuffledChoices: shuffled,
        shuffledCorrectAnswers,
        isMultiple,
      };
    });
  };

  // ✅ Fetch depuis prof_kylian_quiz
  const fetchQuiz = async () => {
    try {
      const { data, error } = await supabase
        .from('prof_kylian_quiz')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching quiz:', error);
        return;
      }

      if (data) {
        setQuiz(data);
        const shuffledQuestions = extractAndShuffleQuestions(data.questions);
        setQuestions(shuffledQuestions);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (index: number) => {
    if (hasAnswered) return;
    
    const currentQuestion = questions[currentIndex];
    
    if (currentQuestion.isMultiple) {
      setSelectedAnswers(prev => {
        if (prev.includes(index)) {
          return prev.filter(i => i !== index);
        } else {
          return [...prev, index];
        }
      });
    } else {
      setSelectedAnswers([index]);
    }
    
    animateSelection();
    if (!isWeb) {
      Vibration.vibrate(10);
    }
  };

  const handleAnswer = () => {
    if (selectedAnswers.length === 0) return;
    
    setHasAnswered(true);
    const currentQuestion = questions[currentIndex];
    const correctAnswers = currentQuestion.shuffledCorrectAnswers;
    
    if (currentQuestion.isMultiple) {
      const correctSelected = selectedAnswers.filter(a => correctAnswers.includes(a)).length;
      const incorrectSelected = selectedAnswers.filter(a => !correctAnswers.includes(a)).length;
      const totalCorrect = correctAnswers.length;
      
      if (correctSelected === totalCorrect && incorrectSelected === 0) {
        setScore(score + 1);
        if (!isWeb) Vibration.vibrate([0, 50, 50, 50]);
      } else if (correctSelected > 0 && incorrectSelected === 0) {
        setScore(score + 0.5);
        if (!isWeb) Vibration.vibrate([0, 30, 30, 30]);
      } else {
        if (!isWeb) Vibration.vibrate(200);
      }
    } else {
      if (selectedAnswers.length === 1 && correctAnswers.includes(selectedAnswers[0])) {
        setScore(score + 1);
        if (!isWeb) Vibration.vibrate([0, 50, 50, 50]);
      } else {
        if (!isWeb) Vibration.vibrate(200);
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      animateQuestionTransition('next', () => {
        setCurrentIndex(currentIndex + 1);
        setSelectedAnswers([]);
        setHasAnswered(false);
      });
    } else {
      setIsFinished(true);
      animateResult();
    }
  };

  const handleRestart = () => {
    if (quiz) {
      const reshuffledQuestions = extractAndShuffleQuestions(quiz.questions);
      setQuestions(reshuffledQuestions);
    }
    
    resultScaleAnim.setValue(0);
    progressAnim.setValue(0);
    setCurrentIndex(0);
    setSelectedAnswers([]);
    setHasAnswered(false);
    setScore(0);
    setIsFinished(false);
    setShowConfetti(false);
  };

  const getOptionStyle = (index: number) => {
    const currentQuestion = questions[currentIndex];
    const correctAnswers = currentQuestion.shuffledCorrectAnswers;
    const isSelected = selectedAnswers.includes(index);
    const isCorrect = correctAnswers.includes(index);
    
    if (!hasAnswered) {
      return isSelected ? styles.optionSelected : styles.option;
    }
    
    if (isCorrect) {
      return styles.optionCorrect;
    }
    
    if (isSelected && !isCorrect) {
      return styles.optionWrong;
    }
    
    return styles.optionDisabled;
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement du quiz...</Text>
      </View>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[colors.indigo600, colors.indigo700]}
            style={styles.headerGradient}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Quiz</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          <View style={[styles.centered, { flex: 1 }]}>
            <LinearGradient
              colors={[colors.indigo100, colors.indigo50]}
              style={styles.emptyIconContainer}
            >
              <Question size={48} color={colors.indigo600} weight="duotone" />
            </LinearGradient>
            <Text style={styles.emptyTitle}>Quiz introuvable</Text>
            <Text style={styles.emptySubtitle}>Ce quiz n'existe pas ou n'a pas de questions.</Text>
            <TouchableOpacity style={styles.emptyButton} onPress={() => router.back()}>
              <Text style={styles.emptyButtonText}>Retour</Text>
            </TouchableOpacity>
          </View>
        </View>
      </>
    );
  }

  // Écran de résultat
  if (isFinished) {
    const percentage = Math.round((score / questions.length) * 100);
    const getResultData = () => {
      if (percentage >= 80) return { icon: Trophy, message: 'Excellent travail !', color: colors.emerald500, bgColor: colors.emerald100 };
      if (percentage >= 60) return { icon: CheckCircle, message: 'Bien joué !', color: colors.indigo600, bgColor: colors.indigo100 };
      if (percentage >= 40) return { icon: Lightbulb, message: 'Continue comme ça !', color: colors.blue500, bgColor: colors.blue100 };
      return { icon: ArrowsClockwise, message: 'Révise encore un peu !', color: colors.rose500, bgColor: colors.rose100 };
    };
    const resultData = getResultData();
    const ResultIcon = resultData.icon;

    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[resultData.color, resultData.color]}
            style={styles.headerGradient}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Résultat</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          
          <ScrollView 
            style={{ flex: 1 }} 
            contentContainerStyle={styles.resultScrollContent}
            showsVerticalScrollIndicator={false}
          >
            <Animated.View style={[
              styles.resultCard,
              { transform: [{ scale: resultScaleAnim }] }
            ]}>
              <LinearGradient
                colors={[resultData.bgColor, resultData.color + '20']}
                style={styles.resultIconContainer}
              >
                <ResultIcon size={56} color={resultData.color} weight="duotone" />
              </LinearGradient>
              
              <Text style={styles.resultTitle}>{resultData.message}</Text>
              
              <View style={styles.scoreContainer}>
                <Text style={[styles.resultScore, { color: resultData.color }]}>
                  {Number.isInteger(score) ? score : score.toFixed(1)}
                </Text>
                <Text style={styles.resultScoreDivider}>/</Text>
                <Text style={styles.resultScoreTotal}>{questions.length}</Text>
              </View>
              
              <View style={[styles.percentageBadge, { backgroundColor: resultData.color }]}>
                <Text style={styles.percentageText}>{percentage}%</Text>
              </View>
              
              <View style={styles.resultButtons}>
                <TouchableOpacity 
                  style={styles.primaryButton}
                  onPress={handleRestart}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.indigo600, colors.indigo700]}
                    style={styles.primaryButtonGradient}
                  >
                    <ArrowsClockwise size={20} color={colors.white} weight="bold" />
                    <Text style={styles.primaryButtonText}>Recommencer ce quiz</Text>
                  </LinearGradient>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.secondaryButton}
                  onPress={() => router.back()}
                  activeOpacity={0.8}
                >
                  <ArrowLeft size={18} color={colors.slate600} weight="bold" />
                  <Text style={styles.secondaryButtonText}>Retour aux contenus</Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </ScrollView>

          {showConfetti && (
            <ConfettiCannon
              ref={confettiRef}
              count={150}
              origin={{ x: SCREEN_WIDTH / 2, y: -20 }}
              autoStart={false}
              fadeOut={true}
              fallSpeed={3000}
              explosionSpeed={350}
              colors={[
                colors.indigo500,
                colors.emerald500,
                colors.purple600,
                colors.rose500,
                '#FFD700',
                '#FF69B4',
              ]}
            />
          )}
        </View>
      </>
    );
  }

  const currentQuestion = questions[currentIndex];
  const choices = currentQuestion.shuffledChoices || [];
  const correctAnswers = currentQuestion.shuffledCorrectAnswers;

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        {/* Header VIOLET */}
        <LinearGradient
          colors={[colors.indigo600, colors.indigo700]}
          style={styles.headerGradient}
        >
          <View style={styles.headerInner}>
            <View style={styles.headerContent}>
              <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                <ArrowLeft size={24} color={colors.white} weight="bold" />
              </TouchableOpacity>
              <Text style={styles.headerTitle} numberOfLines={1}>
                {quiz.title || 'Quiz'}
              </Text>
              <View style={{ width: 40 }} />
            </View>
          </View>
        </LinearGradient>

        <ScrollView 
          style={styles.scrollView} 
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContentOuter}
        >
          <View style={styles.innerContainer}>
            {/* Progress */}
            <View style={styles.progressContainer}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>Question {currentIndex + 1}/{questions.length}</Text>
                <View style={styles.scoreBadge}>
                  <CheckCircle size={16} color={colors.indigo600} weight="duotone" />
                  <Text style={styles.scoreLabel}>
                    {Number.isInteger(score) ? score : score.toFixed(1)}
                  </Text>
                </View>
              </View>
              <View style={styles.progressBar}>
                <Animated.View 
                  style={[
                    styles.progressFill,
                    { 
                      width: progressAnim.interpolate({
                        inputRange: [0, 100],
                        outputRange: ['0%', '100%'],
                      })
                    }
                  ]} 
                />
              </View>
            </View>

            <View style={styles.scrollContent}>
              {/* Question Card */}
              <Animated.View style={[
                styles.questionCard,
                {
                  opacity: fadeAnim,
                  transform: [{ translateX: slideAnim }],
                }
              ]}>
                <View style={styles.questionBadges}>
                  {currentQuestion.isMultiple && (
                    <View style={styles.multipleBadge}>
                      <CheckSquare size={14} color={colors.purple600} weight="bold" />
                      <Text style={styles.multipleText}>Plusieurs réponses</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.questionText}>{currentQuestion.question}</Text>
              </Animated.View>

              {/* Options */}
              <Animated.View style={[
                styles.optionsContainer,
                {
                  opacity: fadeAnim,
                  transform: [{ translateX: slideAnim }],
                }
              ]}>
                {choices.map((choice, index) => {
                  const optionAnim = optionAnims[index] || new Animated.Value(1);
                  const isSelected = selectedAnswers.includes(index);
                  const isCorrect = correctAnswers.includes(index);
                  
                  return (
                    <Animated.View
                      key={index}
                      style={{
                        opacity: optionAnim,
                        transform: [{
                          translateY: optionAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [20, 0],
                          })
                        }]
                      }}
                    >
                      <TouchableOpacity
                        style={getOptionStyle(index)}
                        onPress={() => handleSelectAnswer(index)}
                        activeOpacity={hasAnswered ? 1 : 0.7}
                        disabled={hasAnswered}
                      >
                        <View style={styles.optionContent}>
                          <View style={[
                            currentQuestion.isMultiple ? styles.optionCheckbox : styles.optionLetter,
                            isSelected && !hasAnswered && (currentQuestion.isMultiple ? styles.optionCheckboxSelected : styles.optionLetterSelected),
                            hasAnswered && isCorrect && (currentQuestion.isMultiple ? styles.optionCheckboxCorrect : styles.optionLetterCorrect),
                            hasAnswered && isSelected && !isCorrect && (currentQuestion.isMultiple ? styles.optionCheckboxWrong : styles.optionLetterWrong),
                          ]}>
                            {currentQuestion.isMultiple ? (
                              isSelected || (hasAnswered && isCorrect) ? (
                                <CheckSquare 
                                  size={20} 
                                  color={colors.white} 
                                  weight="bold" 
                                />
                              ) : (
                                <Square 
                                  size={20} 
                                  color={hasAnswered ? colors.slate400 : colors.slate500} 
                                  weight="regular" 
                                />
                              )
                            ) : (
                              <Text style={[
                                styles.optionLetterText,
                                (isSelected && !hasAnswered) && styles.optionLetterTextWhite,
                                (hasAnswered && isCorrect) && styles.optionLetterTextWhite,
                                (hasAnswered && isSelected && !isCorrect) && styles.optionLetterTextWhite,
                              ]}>
                                {String.fromCharCode(65 + index)}
                              </Text>
                            )}
                          </View>
                          <Text style={[
                            styles.optionText,
                            hasAnswered && isCorrect && styles.optionTextCorrect,
                            hasAnswered && isSelected && !isCorrect && styles.optionTextWrong,
                          ]}>
                            {choice}
                          </Text>
                          {hasAnswered && isCorrect && (
                            <CheckCircle size={24} color={colors.emerald500} weight="fill" />
                          )}
                          {hasAnswered && isSelected && !isCorrect && (
                            <XCircle size={24} color={colors.rose500} weight="fill" />
                          )}
                        </View>
                      </TouchableOpacity>
                    </Animated.View>
                  );
                })}
              </Animated.View>

              {/* Explication */}
              {hasAnswered && currentQuestion.explanation && (
                <Animated.View style={[styles.explanationCard, { opacity: fadeAnim }]}>
                  <View style={styles.explanationHeader}>
                    <Lightbulb size={20} color={colors.indigo600} weight="duotone" />
                    <Text style={styles.explanationTitle}>Explication</Text>
                  </View>
                  <Text style={styles.explanationText}>{currentQuestion.explanation}</Text>
                </Animated.View>
              )}

              <View style={{ height: 120 }} />
            </View>
          </View>
        </ScrollView>

        {/* Bottom Bar */}
        <View style={styles.bottomBar}>
          <View style={styles.bottomBarInner}>
            {!hasAnswered ? (
              <TouchableOpacity
                style={[
                  styles.actionButton,
                  selectedAnswers.length === 0 && styles.actionButtonDisabled
                ]}
                onPress={handleAnswer}
                disabled={selectedAnswers.length === 0}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={selectedAnswers.length === 0 ? [colors.slate300, colors.slate300] : [colors.indigo600, colors.indigo700]}
                  style={styles.actionButtonGradient}
                >
                  <Text style={styles.actionButtonText}>Valider ma réponse</Text>
                </LinearGradient>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleNext}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.emerald500, colors.emerald600]}
                  style={styles.actionButtonGradient}
                >
                  <Text style={styles.actionButtonText}>
                    {currentIndex < questions.length - 1 ? 'Question suivante' : 'Voir le résultat'}
                  </Text>
                  <ArrowRight size={20} color={colors.white} weight="bold" />
                </LinearGradient>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 15,
  },

  headerGradient: {
    paddingTop: isWeb ? 24 : 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  headerInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      alignSelf: 'center',
    }),
  },

  progressContainer: {
    padding: 16,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate200,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate900,
  },
  scoreBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  scoreLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.indigo600,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.slate200,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.indigo600,
    borderRadius: 4,
  },

  scrollView: {
    flex: 1,
  },
  scrollContentOuter: {
    flexGrow: 1,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  scrollContent: {
    padding: 16,
  },
  questionCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 24,
    marginBottom: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  questionBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  multipleBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  multipleText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.purple600,
  },
  questionText: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    lineHeight: 28,
  },

  optionsContainer: {
    gap: 12,
  },
  option: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.slate200,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  optionSelected: {
    backgroundColor: colors.indigo50,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.indigo600,
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  optionCorrect: {
    backgroundColor: colors.emerald100,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.emerald500,
  },
  optionWrong: {
    backgroundColor: colors.rose100,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.rose500,
  },
  optionDisabled: {
    backgroundColor: colors.slate50,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.slate200,
    opacity: 0.6,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  optionLetter: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  optionLetterSelected: {
    backgroundColor: colors.indigo600,
  },
  optionLetterCorrect: {
    backgroundColor: colors.emerald500,
  },
  optionLetterWrong: {
    backgroundColor: colors.rose500,
  },
  optionLetterText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate500,
  },
  optionLetterTextWhite: {
    color: colors.white,
  },
  optionCheckbox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    borderWidth: 2,
    borderColor: colors.slate300,
  },
  optionCheckboxSelected: {
    backgroundColor: colors.indigo600,
    borderColor: colors.indigo600,
  },
  optionCheckboxCorrect: {
    backgroundColor: colors.emerald500,
    borderColor: colors.emerald500,
  },
  optionCheckboxWrong: {
    backgroundColor: colors.rose500,
    borderColor: colors.rose500,
  },
  optionText: {
    flex: 1,
    fontSize: 16,
    color: colors.slate900,
    lineHeight: 24,
  },
  optionTextCorrect: {
    color: colors.emerald700,
    fontWeight: '600',
  },
  optionTextWrong: {
    color: colors.rose700,
  },

  explanationCard: {
    backgroundColor: colors.indigo50,
    borderRadius: 16,
    padding: 20,
    marginTop: 20,
    borderLeftWidth: 4,
    borderLeftColor: colors.indigo600,
  },
  explanationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  explanationTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.indigo700,
  },
  explanationText: {
    fontSize: 15,
    color: colors.indigo700,
    lineHeight: 24,
  },

  bottomBar: {
    padding: 16,
    paddingBottom: isWeb ? 16 : 34,
    backgroundColor: colors.white,
    borderTopWidth: 1,
    borderTopColor: colors.slate200,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  bottomBarInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  actionButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  actionButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    gap: 8,
  },
  actionButtonDisabled: {
    opacity: 1,
  },
  actionButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },

  emptyIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  emptyButton: {
    marginTop: 24,
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  emptyButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },

  resultScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  resultCard: {
    backgroundColor: colors.white,
    borderRadius: 28,
    padding: 32,
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 8,
  },
  resultIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  resultTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 20,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 16,
  },
  resultScore: {
    fontSize: 64,
    fontWeight: '800',
  },
  resultScoreDivider: {
    fontSize: 32,
    fontWeight: '300',
    color: colors.slate400,
    marginHorizontal: 8,
  },
  resultScoreTotal: {
    fontSize: 32,
    fontWeight: '600',
    color: colors.slate500,
  },
  percentageBadge: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 20,
    marginBottom: 28,
  },
  percentageText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '700',
  },
  resultButtons: {
    width: '100%',
    gap: 12,
  },
  primaryButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  primaryButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 8,
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    padding: 16,
    backgroundColor: colors.slate100,
    gap: 8,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  secondaryButtonText: {
    color: colors.slate600,
    fontSize: 16,
    fontWeight: '600',
  },
});
